import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Text,
  Badge,
  Skeleton,
  Button,
  Flex,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

interface ClientSummary {
  id: number
  name: string
  code: string
  status: boolean
  users_count: number
  field_engineers_count: number
  assigned_devices_count: number
  last_activity: string | null
  created_at: string | null
}

interface ClientsSummaryTableProps {
  clients: ClientSummary[]
  isLoading?: boolean
}

export function ClientsSummaryTable({ clients, isLoading = false }: ClientsSummaryTableProps) {
  const navigate = useNavigate()

  const formatLastActivity = (timestamp: string | null) => {
    if (!timestamp) return 'Never'
    try {
      const date = new Date(timestamp)
      return formatDistanceToNow(date, { addSuffix: true })
    } catch {
      return 'Unknown'
    }
  }

  const handleRowClick = (clientId: number) => {
    navigate(`/organization/clients/${clientId}`)
  }

  if (isLoading) {
    return (
      <Card bg="white" borderRadius="md" boxShadow="sm">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">Clients Summary</Heading>
          </Flex>
        </CardHeader>
        <CardBody>
          <Skeleton height="300px" width="100%" />
        </CardBody>
      </Card>
    )
  }

  if (!clients || clients.length === 0) {
    return (
      <Card bg="white" borderRadius="md" boxShadow="sm">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">Clients Summary</Heading>
          </Flex>
        </CardHeader>
        <CardBody>
          <Box textAlign="center" py={8}>
            <Text color="gray.500">No clients found</Text>
          </Box>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card bg="white" borderRadius="md" boxShadow="sm">
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
          <Heading size="md">Clients Summary</Heading>
          <Button
            size="xs"
            variant="ghost"
            rightIcon={<ChevronRight size={14} />}
            onClick={() => navigate('/organization/clients')}
          >
            View All
          </Button>
        </Flex>
        <Text fontSize="sm" color="gray.500">
          Overview of all clients with resource counts
        </Text>
      </CardHeader>
      <CardBody pt={0} overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Client Name</Th>
              <Th>Code</Th>
              <Th>Status</Th>
              <Th isNumeric>Users</Th>
              <Th isNumeric>Field Engineers</Th>
              <Th isNumeric>Devices</Th>
              <Th>Last Activity</Th>
            </Tr>
          </Thead>
          <Tbody>
            {clients.map((client) => (
              <Tr
                key={client.id}
                _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                onClick={() => handleRowClick(client.id)}
              >
                <Td fontWeight="medium">{client.name || '—'}</Td>
                <Td>
                  <Text fontSize="xs" color="gray.600">
                    {client.code || '—'}
                  </Text>
                </Td>
                <Td>
                  <Badge colorScheme={client.status ? 'green' : 'gray'} fontSize="xs">
                    {client.status ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td isNumeric>
                  <Text fontWeight="medium">{client.users_count.toLocaleString()}</Text>
                </Td>
                <Td isNumeric>
                  <Text>{client.field_engineers_count.toLocaleString()}</Text>
                </Td>
                <Td isNumeric>
                  <Text>{client.assigned_devices_count.toLocaleString()}</Text>
                </Td>
                <Td>
                  <Text fontSize="xs" color="gray.600">
                    {formatLastActivity(client.last_activity)}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </CardBody>
    </Card>
  )
}
